import { decryptToBlob, decryptToString, deriveKeyFromPassphrase, encryptBlob, encryptString, exportPrivateKeyToBase64, exportPublicKeyToBase64, fromBase64Url, generateAesKey, generateRsaKeyPair, importPrivateKeyFromBase64, importPublicKeyFromBase64, randomBytes, toBase64Url, unwrapKeyForRecipient, wrapKeyForRecipient, } from '@salvobee/crypto-vault';
import { PBKDF2_ITERATIONS, RECOVERY_CODE_GROUPS, RECOVERY_CODE_GROUP_LEN, SALT_BYTES, VERIFIER_BITS, VERIFIER_INFO, } from '../constants/index.js';
const textEncoder = new TextEncoder();
const RECOVERY_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
function concatBytes(...parts) {
    const total = parts.reduce((n, p) => n + p.byteLength, 0);
    const out = new Uint8Array(total);
    let offset = 0;
    for (const p of parts) {
        out.set(p, offset);
        offset += p.byteLength;
    }
    return out;
}
async function deriveVerifierBits(passphrase, salt, iterations) {
    const baseKey = await crypto.subtle.importKey('raw', textEncoder.encode(passphrase), 'PBKDF2', false, ['deriveBits']);
    const verifierSalt = concatBytes(salt, textEncoder.encode(VERIFIER_INFO));
    const bits = await crypto.subtle.deriveBits({ name: 'PBKDF2', hash: 'SHA-256', salt: verifierSalt, iterations }, baseKey, VERIFIER_BITS);
    return new Uint8Array(bits);
}
export function generateSalt() {
    return randomBytes(SALT_BYTES);
}
/**
 * Generate a high-entropy human-readable recovery code: groups of fixed
 * length separated by dashes, drawn from a no-look-alike alphabet.
 */
export function generateRecoveryCode() {
    const totalChars = RECOVERY_CODE_GROUPS * RECOVERY_CODE_GROUP_LEN;
    const bytes = randomBytes(totalChars);
    const chars = new Array(totalChars);
    for (let i = 0; i < totalChars; i++) {
        chars[i] = RECOVERY_ALPHABET[bytes[i] % RECOVERY_ALPHABET.length];
    }
    const groups = [];
    for (let g = 0; g < RECOVERY_CODE_GROUPS; g++) {
        const start = g * RECOVERY_CODE_GROUP_LEN;
        groups.push(chars.slice(start, start + RECOVERY_CODE_GROUP_LEN).join(''));
    }
    return groups.join('-');
}
/**
 * Normalise a user-typed recovery code so derivation matches what we generated:
 * uppercase, strip whitespace and dashes.
 */
export function normaliseRecoveryCode(input) {
    return input.replace(/[\s-]+/g, '').toUpperCase();
}
/**
 * Derive the master key + a domain-separated verifier from the user's passphrase.
 * The master key never leaves the device. The verifier is a one-way value the
 * server can compare to confirm passphrase knowledge without ever seeing it.
 */
export async function deriveMaster(passphrase, salt, iterations = PBKDF2_ITERATIONS) {
    const [masterKey, verifierBytes] = await Promise.all([
        deriveKeyFromPassphrase(passphrase, salt, iterations),
        deriveVerifierBits(passphrase, salt, iterations),
    ]);
    return { masterKey, verifierB64: toBase64Url(verifierBytes) };
}
/**
 * Build a fresh identity (RSA keypair + master + recovery wrap). Both the
 * passphrase and the freshly generated recovery code can independently
 * decrypt the wrapped private key.
 */
export async function generateIdentity(passphrase) {
    const recoveryCode = generateRecoveryCode();
    const salt = generateSalt();
    const saltRecovery = generateSalt();
    const [primary, recovery] = await Promise.all([
        deriveMaster(passphrase, salt),
        deriveMaster(normaliseRecoveryCode(recoveryCode), saltRecovery),
    ]);
    const rsaKeys = await generateRsaKeyPair();
    const rsaPubB64 = await exportPublicKeyToBase64(rsaKeys.publicKey);
    const rsaPrivB64 = await exportPrivateKeyToBase64(rsaKeys.privateKey);
    const [wrappedPriv, wrappedPrivRecovery] = await Promise.all([
        encryptString(rsaPrivB64, primary.masterKey),
        encryptString(rsaPrivB64, recovery.masterKey),
    ]);
    return {
        identity: {
            saltB64: toBase64Url(salt),
            saltRecoveryB64: toBase64Url(saltRecovery),
            rsaPubB64,
            wrappedPriv,
            wrappedPrivRecovery,
            verifierB64: primary.verifierB64,
            verifierRecoveryB64: recovery.verifierB64,
        },
        rsaKeys,
        masterKey: primary.masterKey,
        recoveryCode,
    };
}
export async function unwrapPrivateKey(wrappedPriv, masterKey) {
    const rsaPrivB64 = await decryptToString(wrappedPriv, masterKey);
    return importPrivateKeyFromBase64(rsaPrivB64);
}
/**
 * Re-encrypt the existing RSA private key under a new master derived from a
 * new passphrase. The RSA keypair itself is not rotated — this is a
 * "change passphrase" flow, not an identity rotation.
 */
export async function rebuildKeyMaterialForNewPassphrase(rsaPriv, newPassphrase) {
    const salt = generateSalt();
    const { masterKey, verifierB64 } = await deriveMaster(newPassphrase, salt);
    const rsaPrivB64 = await exportPrivateKeyToBase64(rsaPriv);
    const wrappedPriv = await encryptString(rsaPrivB64, masterKey);
    return { saltB64: toBase64Url(salt), wrappedPriv, verifierB64, masterKey };
}
/**
 * Re-encrypt the existing RSA private key under a freshly generated recovery
 * code. Only call this when the user explicitly opts to rotate the recovery
 * code in tandem with the passphrase.
 */
export async function rebuildRecoveryMaterial(rsaPriv) {
    const recoveryCode = generateRecoveryCode();
    const saltRecovery = generateSalt();
    const { masterKey, verifierB64 } = await deriveMaster(normaliseRecoveryCode(recoveryCode), saltRecovery);
    const rsaPrivB64 = await exportPrivateKeyToBase64(rsaPriv);
    const wrappedPrivRecovery = await encryptString(rsaPrivB64, masterKey);
    return {
        saltRecoveryB64: toBase64Url(saltRecovery),
        wrappedPrivRecovery,
        verifierRecoveryB64: verifierB64,
        recoveryCode,
    };
}
/**
 * Re-derive the master key from a stored salt + a candidate passphrase. Used
 * by the change-passphrase flow to verify the *current* passphrase locally
 * before re-wrapping the RSA private key.
 */
export async function deriveMasterFromStoredSalt(passphrase, saltB64) {
    return deriveMaster(passphrase, fromBase64Url(saltB64));
}
export async function generateAlbumKey() {
    return generateAesKey();
}
export async function wrapAlbumKeyFor(albumKey, recipientPubKeyB64) {
    const pub = await importPublicKeyFromBase64(recipientPubKeyB64);
    return wrapKeyForRecipient(pub, albumKey);
}
export async function unwrapAlbumKey(wrappedAlbumKeyB64, rsaPriv) {
    return unwrapKeyForRecipient(wrappedAlbumKeyB64, rsaPriv);
}
export async function encryptPhoto(blob, albumKey, options = {}) {
    return encryptBlob(blob, albumKey, options);
}
export async function decryptPhoto(packed, albumKey) {
    return decryptToBlob(packed, albumKey);
}
export async function encryptText(text, key) {
    return encryptString(text, key);
}
export async function decryptText(packed, key) {
    return decryptToString(packed, key);
}
export { parsePhotoMeta, } from './photoMeta.js';
//# sourceMappingURL=index.js.map